import * as React from 'react';
import styles from './ResidentHeader.module.css';
import { Panel, Avatar, NumberPanel, NumberPanelProps, Dropdown, Link } from '@ixicare/ui';
import { MoreHorizontal, Phone, SquarePen, Trash2 } from 'lucide-react';
import { t } from '~/i18n';
import { ProfileGender, DeviceEntryProps } from '@ixicare/resources';
import AlertCallModal from '~containers/Alerts/AlertCallModal';
import UserProfileEntry from '~store/Profile/UserProfileEntry';
interface ResidentHeaderProps {
  residentId: number;
  name: string;
  profileImage?: string;
  gender?: ProfileGender;
  numbers: NumberPanelProps[];
  showEditProfile?: boolean;
  siteName?: string;
  profile?: UserProfileEntry;
  device?: DeviceEntryProps;

  onDelete: () => void;
}

const ResidentHeader: React.FC<ResidentHeaderProps> = ({
  name,
  residentId,
  showEditProfile,
  numbers,
  profileImage,
  gender,
  onDelete,
  siteName,
  profile,
  device,
}) => {
  const [showCallModal, setShowCallModal] = React.useState(false);
  const phoneNumbers = React.useMemo(() => {
    const data: { label: string; value: string }[] = [];
    if (profile?.ownedDevice?.phoneNumber) {
      data.push({ label: t('alert.call.devicePhoneNumber'), value: profile?.ownedDevice?.phoneNumber });
    }
    if (profile?.phoneNumber) {
      data.push({ label: t('alert.call.residentPrimaryPhoneNumber'), value: profile.phoneNumber });
    }
    if (profile?.secondaryPhoneNumbers) {
      data.push({ label: t('alert.call.residentSecondaryPhoneNumber'), value: profile.secondaryPhoneNumbers });
    }
    return data;
  }, [alert, profile]);

  const handleCall = React.useCallback(() => {}, [alert]);

  return (
    <>
      <Panel className={styles.Panel}>
        <div className={styles.AvatarWrapper}>
          <Avatar className={styles.Avatar} gender={gender} src={profileImage} />
        </div>
        <div className={styles.ResidentInfo}>
          {(phoneNumbers.length > 0 || showEditProfile) && (
            <div className={styles.Actions}>
              {phoneNumbers.length > 0 && (
                <button type="button" className={styles.Call} onClick={() => setShowCallModal(true)}>
                  <Phone size={24} />
                </button>
              )}
              {showEditProfile && (
                <Dropdown
                  lucide={MoreHorizontal}
                  options={[
                    {
                      label: t('residentDetail.actions.edit'),
                      lucide: SquarePen,
                      route: {
                        href: `/residents/${residentId}/edit`,
                      },
                    },
                    {
                      label: t('residentDetail.actions.delete'),
                      lucide: Trash2,
                      onClick: onDelete,
                    },
                  ]}
                />
              )}
            </div>
          )}

          <h1>{name}</h1>
          <h5>{siteName}</h5>
        </div>
        <div className={styles.Numbers}>
          {numbers.map((num) => (
            <NumberPanel key={`header-numb-${num.icon}`} {...num} withoutBackground />
          ))}
          {device && (
            <Link href={`/device-overview?q=${device.macAddress}#deviceFinder`} className={styles.Device}>
              {device.deviceType && (
                <img className={styles.DeviceImg} src={`/assets/${device.deviceType.toLowerCase()}@2x.png`} alt="" />
              )}
              <div className={styles.DeviceContent}>
                <h5>
                  {device.deviceType
                    ? t(`device.types.${device.deviceType.toLowerCase()}`)
                    : t('devices.table.deviceType')}
                </h5>
                <h6>{`${t('devices.table.macAddress')}: ${device.macAddress}`}</h6>
                {device.imeiNumber && <h6>{`${t('devices.table.imeiNumber')}: ${device.imeiNumber}`}</h6>}
              </div>
            </Link>
          )}
        </div>
      </Panel>
      {showCallModal && (
        <AlertCallModal phoneNumbers={phoneNumbers} onClose={() => setShowCallModal(false)} onCall={handleCall} />
      )}
    </>
  );
};

export default ResidentHeader;
